import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { UnexpectedErrorFallback } from './UnexpectedErrorFallback'
import { preloadPosRoute } from 'utils/preloadPosRoute'
import {
 autoRecoverFromChunkLoadError,
 clearCachesAndReloadForNewDeployment,
 isChunkLoadError,
} from 'helpers/chunkLoadRecovery'

interface Props {
 children: React.ReactNode
}

interface State {
 hasError: boolean
 error: Error | null
 retrying: boolean
}

export class ChunkLoadErrorBoundary extends React.Component<Props, State> {
 constructor(props: Props) {
 super(props)
 this.state = { hasError: false, error: null, retrying: false }
 }

 static getDerivedStateFromError(error: Error): Partial<State> {
 return { hasError: true, error }
 }

 componentDidCatch(error: Error, info: React.ErrorInfo) {
 if (isChunkLoadError(error)) {
 void autoRecoverFromChunkLoadError()
 } else {
 console.error('ChunkLoadErrorBoundary caught non-chunk error:', error, info)
 }
 }

 handleRetry = async () => {
 this.setState({ retrying: true })
 await preloadPosRoute()
 this.setState({ hasError: false, error: null, retrying: false })
 }

 handleReload = () => {
 void clearCachesAndReloadForNewDeployment()
 }

 render() {
 if (!this.state.hasError || !this.state.error) {
 return this.props.children
 }

 const error = this.state.error
 const isChunk = isChunkLoadError(error)
 const isOffline = typeof navigator !== 'undefined' && !navigator.onLine

 if (!isChunk) {
 return <UnexpectedErrorFallback onRetry={this.handleReload} />
 }

 return (
 <ChunkLoadFallback
 isOffline={isOffline}
 retrying={this.state.retrying}
 onRetry={this.handleRetry}
 onReload={this.handleReload}
 />
 )
 }
}

function ChunkLoadFallback({
 isOffline,
 retrying,
 onRetry,
 onReload,
}: {
 isOffline: boolean
 retrying: boolean
 onRetry: () => void
 onReload: () => void
}) {
 const navigate = useNavigate()
 const { t } = useTranslation()

 return (
 <div className="flex flex-col items-center justify-center min-h-[50vh] p-8 text-center" dir="rtl">
 <div className="w-16 h-16 mb-4 rounded-full bg-amber-100 dark:bg-amber-950 flex items-center justify-center">
 <svg className="w-8 h-8 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728M5.636 18.364a9 9 0 010-12.728M12 2v4m0 12v4m-7.071-3.929l2.828-2.828m8.486-8.486l2.828-2.828M2 12h4m12 0h4m-3.929 7.071l-2.828-2.828M7.757 7.757L4.929 4.929"/>
 </svg>
 </div>

 <h3 className="text-lg font-semibold text-foreground mb-2">
 {t('common.chunkLoadError.title')}
 </h3>

 <p className="text-muted-foreground mb-2 max-w-md">
 {isOffline
 ? t('common.chunkLoadError.offlineDescription')
 : t('common.chunkLoadError.onlineDescription')}
 </p>

 <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
 <button
 onClick={onRetry}
 disabled={retrying}
 className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary-hover transition-colors disabled:">
 {retrying ? t('common.chunkLoadError.retrying') : t('common.chunkLoadError.retry')}
 </button>
 <button
 onClick={onReload}
 className="px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors">
 {t('common.chunkLoadError.reloadApp')}
 </button>
 <button
 onClick={() => navigate('/')}
 className="px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors">
 {t('common.chunkLoadError.goHome')}
 </button>
 </div>
 </div>
 )
}
